/**
 * Per-campaign permissions hook — backs the Advanced sub-tab's "Available
 * for" table. Reads the {read, modify} user lists and writes back when the
 * admin flips a row.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';
import { binomKeys } from './useBinom';

export interface BinomCampaignPermissionUser {
  id: number;
  name: string;
  role: string;
}

export interface BinomCampaignPermissions {
  read: BinomCampaignPermissionUser[];
  modify: BinomCampaignPermissionUser[];
}

const permKey = (id: string) =>
  [...binomKeys.campaign(id), 'permissions'] as const;

export function useBinomPermissions(id: string | undefined) {
  return useQuery({
    queryKey: permKey(id ?? ''),
    queryFn: async () => {
      const r = await api.get<{ data: BinomCampaignPermissions | null }>(
        `/binom/campaigns/${encodeURIComponent(id!)}/permissions`,
      );
      return r.data?.data ?? null;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useUpdateBinomPermissions(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BinomCampaignPermissions) => {
      await api.put(
        `/binom/campaigns/${encodeURIComponent(id!)}/permissions`,
        body,
      );
    },
    onSuccess: () => {
      if (id) qc.invalidateQueries({ queryKey: permKey(id) });
    },
  });
}
